import { CheckCircleFilled, EyeFilled, UploadOutlined, WarningFilled } from '@ant-design/icons'
import { Button, Tooltip } from 'gokwik-ui-kit'
import { useState } from 'react'
import { getDocumentDownloaded } from '@library/utilities/helpers/helper'
import { getUserData } from '@store/user/selectors'
import { useSelector } from 'react-redux'
import { newStepConfiguration } from '@pages/onboarding/stepsConfig'

const DocsUploadCard = ({
    doc,
    onShowSampleClick,
    openUploadModal,
    handlePreview,
    uploadedFileName,
    resolvedErrors,
    setResolvedErrors,
    deleteFile,
    hasGenericError,
}) => {
    const [isHovering, setIsHovering] = useState(false)
    const userData = useSelector(getUserData)

    const handleMouseEnter = () => {
        setIsHovering(true)
    }

    const handleMouseLeave = () => {
        setIsHovering(false)
    }
    const { title, desc, logo, label } = doc

    const getErrors = (item) => {
        const kycErrorList = Object.keys(userData?.kycData?.kyc_failure_reasons)

        if (kycErrorList?.includes(item.name)) {
            return {
                validateStatus: 'error',
                help: Array.isArray(userData?.kycData?.kyc_failure_reasons[item?.name])
                    ? userData?.kycData?.kyc_failure_reasons[item?.name][0]
                    : userData?.kycData?.kyc_failure_reasons[item?.name],
            }
        } else {
            return {
                validateStatus: '',
                help: '',
            }
        }
    }

    const isFieldEnabled = (fieldName) => {
        const undeterminedKycErrorState =
            userData?.merchantDetails?.onboarding_status?.kwik_checkout_app === 'kyc_verification_failed' &&
            userData?.kycData?.kyc_rejection_reason

        if (userData?.merchantDetails?.onboarding_status?.kwik_checkout_app === 'kyc_verification_failed') {
            if (Object.keys(userData?.kycData?.kyc_failure_reasons)?.includes(fieldName) || undeterminedKycErrorState) {
                return false //dont-disable-the-field
            } else {
                return true //yes-disable-the-field
            }
        } else if (
            userData?.merchantDetails?.onboarding_status?.kwik_checkout_app === 'kyc_verification_pending' ||
            userData?.merchantDetails?.onboarding_status?.kwik_checkout_app === 'kyc_completed'
        ) {
            return true //yes-disable-the-field
        }
    }

    return (
        <>
            <div
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                className='relative flex items-center justify-between border border-[#B4B4B4] rounded-xl p-4 w-full bg-white overflow-hidden'
            >
                <div className='flex items-center gap-4'>
                    <img src={logo} className='flex mx-auto' />
                    <div>
                        <div className='font-semibold text-base flex gap-1 items-center'>
                            {/* <div onClick={() => deleteFile(doc?.details?.name)}>X</div> */}
                            <p>
                                <span className='mr-1'>{label}</span>
                                {doc?.details?.uploaded && (
                                    <>
                                        &nbsp;
                                        {userData?.merchantDetails?.onboarding_status?.kwik_checkout_app ===
                                            'kyc_verification_failed' &&
                                        !resolvedErrors?.includes(doc?.details?.name) &&
                                        getErrors(doc?.details)?.help ? (
                                            <span className='text-red-500 text-sm cursor-pointer'>
                                                <Tooltip title={<>{getErrors(doc?.details)?.help}</>}>
                                                    <WarningFilled />
                                                </Tooltip>
                                            </span>
                                        ) : (
                                            doc?.details?.uploaded && (
                                                <span>
                                                    <CheckCircleFilled className='text-[#17B26A]' />
                                                </span>
                                            )
                                        )}
                                    </>
                                )}
                            </p>
                        </div>
                        <div className='text-xs font-normal text-[#B4B4B4]'>
                            {/* {doc?.details?.uploaded ? uploadedFileName : desc} //ask to implement this from BE so that we can get the name of uploaded doc*/}
                            {desc}
                        </div>
                    </div>
                </div>
                <div className='flex gap-1'>
                    <Button
                        icon={<UploadOutlined />}
                        className='border-[#00000026] border-solid z-[7]'
                        variant={doc?.details?.uploaded ? 'default' : 'primary'}
                        onClick={() => openUploadModal(doc)}
                        disabled={
                            !hasGenericError(userData?.kycData?.kyc_failure_reasons, [
                                'business_documents_generic_error',
                            ]) &&
                            !Object.keys(userData?.kycData?.kyc_failure_reasons)?.includes(doc?.details?.name) &&
                            isFieldEnabled(doc?.details?.name)
                        }
                    >
                        {doc?.details?.uploaded ? 'Re-Upload' : 'Upload'}
                    </Button>
                    {doc?.details?.uploaded && (
                        <Button
                            variant='default'
                            className='border-[#00000026] border-solid'
                            onClick={() => handlePreview(doc?.details?.name)}
                        >
                            <EyeFilled />
                        </Button>
                    )}
                </div>

                {isHovering && !doc?.details?.uploaded && (
                    <div className='absolute inset-0 flex items-center justify-end gap-4 z-[5] w-[85%]'>
                        {doc?.details?.show_preview_document && (
                            <div className='backdrop-blur-sm bg-white/80 rounded-lg p-1'>
                                <Button
                                    onClick={() => onShowSampleClick(doc)}
                                    variant='default'
                                    className='whitespace-nowrap border border-gray-300'
                                >
                                    View Sample
                                </Button>
                            </div>
                        )}

                        {doc?.details?.sample_download_url && (
                            <div className='backdrop-blur-sm bg-white/80 rounded-lg p-1'>
                                <Button
                                    onClick={() =>
                                        getDocumentDownloaded(doc?.details?.sample_download_url, doc?.details?.name)
                                    }
                                    variant='default'
                                    className='whitespace-nowrap border border-gray-300 text-primary'
                                >
                                    Download Template
                                </Button>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </>
    )
}

export default DocsUploadCard
